import { useState, type ReactNode, cloneElement, isValidElement } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogContent,
  DialogClose,
} from '@src/design-system/dialog';
import { Button } from '@src/design-system/button';
import { cn } from '@src/design-system/helpers';
import { CloseCircle, Maximize4 } from 'iconsax-react';

// Configurable height constants
export const CARD_HEIGHTS = {
  normal: '430px',
  modal: '600px',
} as const;

interface ChartModalWrapperProps {
  children: ReactNode;
  className?: string;
  modalCardHeight?: string;
}

export const ChartModalWrapper = ({
  children,
  className,
  modalCardHeight = CARD_HEIGHTS.modal,
}: ChartModalWrapperProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Create the button component based on isOpen state
  const modalButton = isOpen ? (
    <DialogClose asChild>
      <Button
        variant='primaryOutline'
        size='sm'
        className='ml-auto border-primary-200 gap-x-1'
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(false);
        }}
      >
        <span className='text-primary-1000 text-sm font-medium'>Close</span>
        <CloseCircle className='w-[14px]! h-[14px]!' />
      </Button>
    </DialogClose>
  ) : (
    <DialogTrigger asChild>
      <button
        className='ml-auto w-[38px] h-[35px] flex items-center justify-center rounded-xl hover:bg-gray-100 border border-primary-200 transition-colors cursor-pointer'
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        <Maximize4
          size={18}
          className='text-primary-1000'
        />
      </button>
    </DialogTrigger>
  );

  const openExpand = () => setIsOpen(true);

  const childrenWithProps = isValidElement(children)
    ? cloneElement(children as React.ReactElement, {
        modalButton,
        isModalOpen: false,
        modalCardHeight,
        onExpand: openExpand,
      })
    : children;

  // Pass props to children for modal view (isModalOpen: true)
  const childrenWithModalProps = isValidElement(children)
    ? cloneElement(children as React.ReactElement, {
        modalButton,
        isModalOpen: true,
        modalCardHeight,
        onExpand: undefined,
      })
    : children;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <div className={cn('relative w-full', className)}>
        {childrenWithProps}
      </div>

      <DialogPortal>
        {/* <DialogOverlay className='bg-[rgba(0, 0, 0, 0.1)]' /> */}
        <DialogContent
          dialogOverlayProps={{ className: 'bg-black/40' }}
          align='center'
          className='h-fit max-w-[80vw] border-none rounded-2xl'
        >
          <div className='flex-1 overflow-auto'>{childrenWithModalProps}</div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
